import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output
} from '@angular/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { MessageService } from 'primeng/api';
import { ConfigModel } from 'src/app/models/config.model';
import { DeviceModel } from 'src/app/models/device.model';
import { OrgModel } from 'src/app/models/org.model';
import { VoiceConfigModel } from 'src/app/models/voice-config.model';
import { ApiService, parseApiErrorsToToast } from 'src/app/services/api.service';
import { DeviceShadowUtils } from 'src/app/util/device-shadow-utils';

@Component({
  selector: 'app-device-assign-config',
  templateUrl: './assign-config.component.html',
  styleUrls: ['./assign-config.component.scss']
})
export class DeviceAssignConfigComponent implements OnInit, OnChanges {
  @BlockUI() blockUI: NgBlockUI | undefined;
  @Input() visible = false;
  @Input() devices: DeviceModel[] = [];
  @Output() onClose: EventEmitter<any> = new EventEmitter();
  @Output() visibleChange: EventEmitter<any> = new EventEmitter();

  isLoading = false;
  deviceConfigs: ConfigModel[] = [];
  deviceConfigSelected?: ConfigModel;

  voiceConfigs: VoiceConfigModel[] = [];
  voiceConfigSelected?: VoiceConfigModel;

  sameOrgSelected = true;
  selectedOrgId?: string;
  selectedOrgName?: string;
  orgWarnings: string[] = [];

  constructor(
    private api: ApiService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.evaluateDevicesAndLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['devices'] || changes['visible']) {
      this.evaluateDevicesAndLoad();
    }
  }

  async evaluateDevicesAndLoad() {
    this.deviceConfigSelected = undefined;
    this.voiceConfigSelected = undefined;
    this.deviceConfigs = [];
    this.voiceConfigs = [];
    this.sameOrgSelected = true;
    this.selectedOrgId = undefined;
    this.selectedOrgName = undefined;
    this.orgWarnings = [];

    if (!this.visible || !this.devices?.length) {
      return;
    }

    const orgIdsRaw = this.devices.map(d => d.orgId);
    const hasMissingOrg = orgIdsRaw.some(id => id === null || id === undefined);

    const assignedOrgIds = [
      ...new Set(
        orgIdsRaw.filter((id): id is string => id !== null && id !== undefined)
      )
    ];

    this.orgWarnings = [];

    if (hasMissingOrg) {
      this.orgWarnings.push('You must assign ORG first.');
    }

    const shouldShowSameOrgWarning =
      assignedOrgIds.length > 1 || (hasMissingOrg && assignedOrgIds.length >= 1);

    if (shouldShowSameOrgWarning) {
      this.orgWarnings.push('All selected devices must belong to the same ORG before assigning Profiles.');
    }

    if (this.orgWarnings.length > 0) {
      this.sameOrgSelected = false;
      return;
    }

    this.selectedOrgId = assignedOrgIds[0];
    this.selectedOrgName = this.devices[0]?.org?.name;

    await this.loadProfilesForOrg(this.selectedOrgId);
  }

  async loadProfilesForOrg(orgId: string) {
    try {
      this.isLoading = true;

      const rspConfig = await this.api.get<{ configs: ConfigModel[] }>(`/configs/latest/${orgId}`);
      console.log('loadProfilesForOrg() - rspConfig.configs = ', rspConfig .configs)
      this.deviceConfigs = (rspConfig.configs ?? []).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      const rspVConfig = await this.api.get<{ voiceConfigs: VoiceConfigModel[] }>(`/voice-configs/latest/${orgId}`);
      console.log('loadProfilesForOrg() - rspVConfig.voiceConfigs = ', rspVConfig.voiceConfigs)
      this.voiceConfigs = (rspVConfig.voiceConfigs ?? []).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    } finally {
      this.isLoading = false;
    }
  }

  async assign() {
    const hasMissingOrg = this.devices.some(d => !d.orgId);

    const assignedOrgIds = [
      ...new Set(
        this.devices
          .map(d => d.orgId)
          .filter((id): id is string => id !== null && id !== undefined)
      )
    ];

    const warnings: string[] = [];

    if (hasMissingOrg) {
      warnings.push('You must assign an ORG first, before assigning Profiles.');
    }

    if (assignedOrgIds.length > 1) {
      warnings.push('Selected devices must ALL belong to the same ORG before assigning Profiles.');
    }

    if (warnings.length) {
      for (const warning of warnings) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Profile Assignment Blocked',
          detail: warning
        });
      }
      return;
    }

    try {
      this.blockUI?.start();

      await Promise.all(this.devices.map(device =>
        this.api.put(`/devices/${device.id}`, {
          assetTag: device.assetTag,
          orgId: device.orgId,
          deviceConfigId: this.deviceConfigSelected?.id ?? device.deviceConfigId ?? null,
          voiceConfigId: this.voiceConfigSelected?.id ?? device.voiceConfigId ?? null,
        })
      ));

      const clearedShadows = {
        ...DeviceShadowUtils.buildClearedDeviceConfigShadows(),
        ...DeviceShadowUtils.buildClearedVoiceConfigShadows()
      };

      let selectedDeviceConfigShadows: any = {};
      let selectedVoiceConfigShadows: any = {};

      if (this.deviceConfigSelected?.id) {
        const configChosen = await this.api.get<ConfigModel>(`/configs/${this.deviceConfigSelected.id}`);
        selectedDeviceConfigShadows = configChosen?.config?.shadows || {};
      }

      if (this.voiceConfigSelected?.id) {
        const vConfigChosen = await this.api.get<VoiceConfigModel>(`/voice-configs/${this.voiceConfigSelected.id}`);
        selectedVoiceConfigShadows = vConfigChosen?.config?.shadows || {};
      }

      const finalShadowData = {
        ...clearedShadows,
        ...selectedDeviceConfigShadows,
        ...selectedVoiceConfigShadows
      };

      for (const device of this.devices) {
        await this.api.put(`/things/${device.id}/shadows`, finalShadowData);
      }

      this.deviceConfigSelected = undefined;
      this.voiceConfigSelected = undefined;
      this.onClose.emit();
      this.visible = false;
      this.visibleChange.emit(false);
    } catch (err) {
      this.messageService.addAll(parseApiErrorsToToast(err));
    } finally {
      this.blockUI?.stop();
    }
  }

  async onCloseDialog() {
    this.deviceConfigSelected = undefined;
    this.voiceConfigSelected = undefined;
    this.deviceConfigs = [];
    this.voiceConfigs = [];
    this.sameOrgSelected = true;
    this.selectedOrgId = undefined;
    this.selectedOrgName = undefined;
    this.orgWarnings = [];
  }
}
